import { memo } from 'react';
import { Typography } from '../../../../../components/typography/typography';
import { EditableField } from '../../../../../components/field/field';
import { ProductItemState } from '../../hooks/use-product-item-data';
import { RentalProductPrice } from '../../../../../clients/quipli-service/pricing/pricing';
import { displayMoneyFormat } from '../../../../../util/util';
import { Currency } from 'dinero.js';
import deepEqual from 'deep-equal';
import {
  ProductItemStateRentalRates,
  ProductItemStateRentalRatesReturn,
} from '../../hooks/use-product-item-rental-rates';

interface ProductItemRatesProps
  extends Pick<ProductItemState, 'isRental' | 'isEditing' | 'rentalPrices' | 'customRentalRates'> {
  isShowingPrices: boolean;
  isCustomOrderRentalRatesEnabled: boolean;
  currency: string | Currency;
  updateRate: ProductItemStateRentalRatesReturn['updateRate'];
  resetRate: ProductItemStateRentalRatesReturn['resetRate'];
}

export const ProductItemRates = memo<ProductItemRatesProps>(
  ({
    isRental,
    isShowingPrices,
    isEditing,
    rentalPrices,
    isCustomOrderRentalRatesEnabled,
    currency,
    customRentalRates,
    updateRate,
    resetRate,
  }) => {
    // Only show rates for rental products when prices are being shown
    if (!isRental || !isShowingPrices || !rentalPrices.length) {
      return null;
    }

    return (
      <div className="flex-[0_1_calc(100%-20px)] show-prices">
        <div className="invoice-table m-[10px] min-w-full" data-cy="item-table-rates-container">
          <div className="grid grid-flow-col gap-y-2 gap-x-2">
            {rentalPrices.map((price: RentalProductPrice) => (
              <ProductItemRate
                key={`${price.productId}-${price.durationUnit}-${price.durationValue}`}
                price={price}
                customRentalRates={customRentalRates}
                isCustomOrderRentalRatesEnabled={isCustomOrderRentalRatesEnabled}
                isEditing={isEditing}
                currency={currency}
                updateRate={updateRate}
                resetRate={resetRate}
              />
            ))}
          </div>
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.isShowingPrices === nextProps.isShowingPrices &&
      prevProps.isEditing === nextProps.isEditing &&
      prevProps.currency === nextProps.currency &&
      deepEqual(prevProps.customRentalRates, nextProps.customRentalRates) &&
      deepEqual(prevProps.rentalPrices, nextProps.rentalPrices)
    );
  },
);

ProductItemRates.displayName = 'ProductItemRates';

// Individual rate component for better organization
const ProductItemRate = memo<{
  price: RentalProductPrice;
  customRentalRates: ProductItemStateRentalRates;
  isCustomOrderRentalRatesEnabled: boolean;
  isEditing: boolean;
  currency: string | Currency;
  updateRate: ProductItemStateRentalRatesReturn['updateRate'];
  resetRate: ProductItemStateRentalRatesReturn['resetRate'];
}>(
  ({ price, customRentalRates, isCustomOrderRentalRatesEnabled, isEditing, currency, updateRate, resetRate }) => {
    // Find the editable field value
    const editableFieldVal =
      customRentalRates.current.find(
        (r) =>
          r.productId === price.productId &&
          r.durationUnit === price.durationUnit &&
          r.durationValue === price.durationValue,
      ) ??
      customRentalRates.original.find(
        (r) =>
          r.productId === price.productId &&
          r.durationUnit === price.durationUnit &&
          r.durationValue === price.durationValue,
      ) ??
      price;

    const canReset =
      (!!price.originalPrice && editableFieldVal.price !== price.originalPrice.price) ||
      (!price.originalPrice && editableFieldVal.price !== price.price);

    // Only show edit mode when custom order rental rates are enabled and the product is being edited
    const isEditMode = isCustomOrderRentalRatesEnabled && isEditing;

    const hasEditedPrice = price.originalPrice && price.originalPrice.price !== editableFieldVal.price;
    // Don't show current price in edit mode since EditableField will display it
    const shouldShowCurrentPrice = !isEditMode;

    return (
      <div className="pt-1 px-4">
        <Typography
          type="text-sm"
          weight="regular"
          className="text-[var(--grey-500)] border-solid border-t-0 border-x-0 border-b border-b-[var(--grey-200)]"
          data-cy="item-rate-label"
        >
          {price.durationLabel}
        </Typography>

        <div className="py-2">
          <Typography type="text-sm" className="text-quipli-black-800">
            {hasEditedPrice && (
              <s className="mr-1" data-cy="original-price">
                {displayMoneyFormat(price.originalPrice!.price / 100, currency)}
              </s>
            )}
            {shouldShowCurrentPrice && (
              <span data-cy="current-price">{displayMoneyFormat(price.price / 100, currency)}</span>
            )}
          </Typography>

          {isEditMode && (
            <EditableField
              type="number"
              value={editableFieldVal.price / 100}
              formattedValue={displayMoneyFormat(editableFieldVal.price / 100, currency)}
              onChange={(value) => updateRate(price, value)}
              canReset={canReset}
              resetPosition="right"
              onReset={() => resetRate(price)}
            />
          )}
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.isEditing === nextProps.isEditing &&
      prevProps.currency === nextProps.currency &&
      deepEqual(prevProps.customRentalRates, nextProps.customRentalRates) &&
      deepEqual(prevProps.price, nextProps.price)
    );
  },
);

ProductItemRate.displayName = 'ProductItemRate';
